import { CircleAlert } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssessmentCard from './AssessmentCard';
import type {
    TraineeAssessmentModuleInstanceLaunchItemDataFragment,
    TraineeAssessmentRunsDataFragment,
} from './TraineeAssessment.api';
import { AssessmentRunStatus, ModuleSubCategory, UserRole } from '@/apolloClient.types';
import ResultDrawer from '@/components/blocks/AssessmentResults/ResultDrawer';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import Loader from '@/components/common/Loader';
import Tab from '@/components/common/Tab';
import TableFooter from '@/components/common/TableFooter';
import { useUser } from '@/contexts/UserSession';
import useDateFormats from '@/utils/useDateFormats';

const pageSizeOptions = [12, 24, 36, 48];

interface ModuleListViewProps {
    assessmentRun: TraineeAssessmentRunsDataFragment | null;
    launchItems: TraineeAssessmentModuleInstanceLaunchItemDataFragment[];
    totalCount: number;
    courseInstanceId: string;
    loading?: boolean;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    activeSubCategory: ModuleSubCategory;
    onSubCategoryChange: (subCategory: ModuleSubCategory) => void;
}

const ModuleListView = ({
    assessmentRun,
    launchItems,
    totalCount,
    courseInstanceId,
    loading,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    activeSubCategory,
    onSubCategoryChange,
}: ModuleListViewProps) => {
    const { t } = useTranslation('traineeAssessmentList');
    const { formatLongDate } = useDateFormats();
    const [isShowDrawer, setIsShowDrawer] = useState(false);
    const [selectedResult, setSelectedResult] = useState<string>('');
    const user = useUser(true);

    const expiredOn = useMemo(() => {
        if (!assessmentRun?.endDate) {
            return new Date();
        }
        return new Date(assessmentRun.endDate);
    }, [assessmentRun?.endDate]);

    const subcategoryTabs = useMemo(() => {
        return [
            {
                name: t('subcategories.cognitive'),
                key: ModuleSubCategory.Cognitive,
                current: activeSubCategory === ModuleSubCategory.Cognitive,
            },
            {
                name: t('subcategories.psychological'),
                key: ModuleSubCategory.Psychological,
                current: activeSubCategory === ModuleSubCategory.Psychological,
            },
        ];
    }, [t, activeSubCategory]);

    const filteredLaunchItems = useMemo(() => {
        return launchItems.filter(item => {
            return (
                (item.moduleInstance?.moduleDefinition?.__typename === 'AssessmentModuleDefinition' ||
                    item.moduleInstance?.moduleDefinition?.__typename === 'TrainingModuleDefinition') &&
                item.moduleInstance?.moduleDefinition?.subCategory === activeSubCategory
            );
        });
    }, [launchItems, activeSubCategory]);

    const onViewResult = useCallback((resultId: string | undefined) => {
        if (resultId) {
            setSelectedResult(resultId);
            setIsShowDrawer(true);
        }
    }, []);

    const validTabs = useMemo(() => {
        // Show Psychological tab only for trainers
        if (user.__typename === 'TenantUser' && user.role === UserRole.Trainer) {
            return subcategoryTabs.filter(tab => tab.key === ModuleSubCategory.Psychological);
        }

        return subcategoryTabs;
    }, [subcategoryTabs, user]);

    const handleTabChange = useCallback(
        (key: string) => {
            const next = key as ModuleSubCategory;
            onSubCategoryChange(next);
        },
        [onSubCategoryChange]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            onPageChange(newPage);
        },
        [onPageChange]
    );

    const handlePageSizeChange = useCallback(
        (newPageSize: number) => {
            onPageSizeChange(newPageSize);
        },
        [onPageSizeChange]
    );

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            {assessmentRun?.status === AssessmentRunStatus.Ended && (
                <div className="mb-5 bg-teal-50 p-4">
                    <div>
                        <div className="flex gap-2.5 text-base font-bold text-neutral-900">
                            <div className="flex flex-col justify-center">
                                <CircleAlert className="size-4" color="teal" />
                            </div>
                            {t('expirationMessage', {
                                completed: `${filteredLaunchItems.filter(i => i.isCompleted).length}/${filteredLaunchItems.length}`,
                            })}
                        </div>
                        <p className="mt-2 text-base font-medium text-neutral-500">
                            {t('expirationDescription', {
                                expiredOn: formatLongDate(expiredOn),
                            })}
                        </p>
                    </div>
                </div>
            )}
            <div>
                {validTabs?.length > 0 && <Tab tabs={validTabs} onChange={handleTabChange} />}

                {filteredLaunchItems.length > 0 ? (
                    <div className="mt-5 space-y-6">
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {filteredLaunchItems.map(item => (
                                <AssessmentCard
                                    expiredOn={expiredOn}
                                    assessmentRunStatus={assessmentRun?.status || AssessmentRunStatus.NotStarted}
                                    key={`${item.moduleInstance.id}:${item.targetUserId}`}
                                    moduleInstance={item.moduleInstance}
                                    isCompleted={item.isCompleted}
                                    completedAt={item.completedAt || null}
                                    assessmentRunId={assessmentRun?.id || ''}
                                    courseInstanceId={courseInstanceId}
                                    targetUserId={item.targetUserId}
                                    targetFullName={item.targetFullName}
                                    resultId={item.resultId || undefined}
                                    onViewResult={onViewResult}
                                />
                            ))}
                        </div>

                        {totalCount === 0 ? (
                            <EmptyContainer>
                                <Empty title={t('listing.noAssessments')} />
                            </EmptyContainer>
                        ) : (
                            <TableFooter
                                currentPage={page}
                                pageSize={pageSize}
                                totalCount={totalCount}
                                pageSizeOptions={pageSizeOptions}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        )}
                    </div>
                ) : (
                    <div className="mt-16 flex w-full justify-center rounded-xl bg-white lg:p-40">
                        <div className="max-w-lg">
                            <div className="text-center">
                                <CircleAlert className="mx-auto h-12 w-12 text-gray-500" strokeWidth={1} />
                                <div className="mt-5 text-base font-medium text-neutral-900">
                                    {t('noAssessmentFoundTitle')}
                                </div>
                                <p className="mt-1 text-base text-gray-500">{t('noAssessmentFoundDescription')}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ResultDrawer open={isShowDrawer} onClose={() => setIsShowDrawer(false)} resultId={selectedResult} />
        </>
    );
};
export default ModuleListView;
